import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Music, VolumeX } from 'lucide-react';

const TRACK = '/audio/ambient.mp3';
const STORE_KEY = 'es_music_on';
const VOLUME = 0.35; // background, not foreground

/** Music is ON unless the visitor explicitly turned it off before. */
function wantsMusic() {
  try {
    return localStorage.getItem(STORE_KEY) !== '0';
  } catch {
    return true; // private mode — fall back to the default
  }
}

/**
 * Looping background track, on by default, brought in on the first scroll up.
 *
 * Browsers refuse audible playback until the page has "user activation", so the
 * track starts muted (which is allowed) and we try to unmute on each gesture.
 *
 * Two things that make this actually work:
 *  1. We pause before unmuting. Calling play() on an element that is already
 *     playing resolves immediately no matter what the autoplay policy says, so
 *     it cannot be used to detect permission. Pausing forces a real request.
 *  2. We re-check `paused`/`muted` on the next frame, because Chrome applies
 *     the policy asynchronously and may pause us right after play() resolves.
 */
export default function BackgroundMusic() {
  const audioRef = useRef(null);
  const lastYRef = useRef(0);
  const doneRef = useRef(false);
  const [available, setAvailable] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const startMuted = useCallback((el) => {
    el.muted = true;
    el.play().catch(() => {});
  }, []);

  const tryUnmute = useCallback(async () => {
    const el = audioRef.current;
    if (!el || doneRef.current || !wantsMusic()) return false;

    try {
      el.pause();          // force play() to be evaluated afresh
      el.muted = false;
      el.volume = VOLUME;
      await el.play();
    } catch {
      startMuted(el);
      setBlocked(true);
      return false;
    }

    // Chrome can pause us a tick after play() resolves — confirm on next frame.
    const ok = await new Promise((resolve) =>
      requestAnimationFrame(() =>
        resolve(!el.paused && !el.muted && el.currentTime >= 0)
      )
    );

    if (!ok) {
      startMuted(el);
      setBlocked(true);
      return false;
    }

    doneRef.current = true;
    setBlocked(false);
    setPlaying(true);
    return true;
  }, [startMuted]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !wantsMusic()) return undefined;

    startMuted(el);
    lastYRef.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      const up = y < lastYRef.current;
      lastYRef.current = y;
      if (up) attempt();
    };

    const attempt = async () => {
      if (await tryUnmute()) detach();
    };

    // Scroll up is the requested trigger. The pointer/key events stay as a
    // fallback because Chrome does not treat scrolling as user activation.
    const gestures = ['pointerdown', 'touchstart', 'keydown', 'click'];

    window.addEventListener('scroll', onScroll, { passive: true });
    gestures.forEach((e) =>
      window.addEventListener(e, attempt, { passive: true })
    );

    function detach() {
      window.removeEventListener('scroll', onScroll);
      gestures.forEach((e) => window.removeEventListener(e, attempt));
    }
    return detach;
  }, [tryUnmute, startMuted]);

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;

    if (playing) {
      el.pause();
      setPlaying(false);
      doneRef.current = false;
      try { localStorage.setItem(STORE_KEY, '0'); } catch { /* ignore */ }
      return;
    }

    try { localStorage.setItem(STORE_KEY, '1'); } catch { /* ignore */ }
    doneRef.current = false;
    // A click is user activation, so this path is reliable.
    await tryUnmute();
  };

  if (!available) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={TRACK}
        loop
        autoPlay
        muted
        preload="auto"
        onPause={() => setPlaying(false)}
        onError={() => setAvailable(false)}
      />
      <motion.button
        type="button"
        onClick={toggle}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.1 }}
        className="fixed bottom-6 left-6 z-40 grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-ink-900/80 text-white shadow-lg backdrop-blur transition-colors hover:border-white/40"
        aria-label={playing ? 'Turn background music off' : 'Turn background music on'}
        title={playing ? 'Music on' : blocked ? 'Tap for sound' : 'Music off'}
      >
        {playing ? (
          <>
            <span className="absolute inset-0 animate-ping rounded-full bg-brand/20" />
            <Music size={20} className="relative text-brand" />
          </>
        ) : (
          <>
            {blocked && (
              <span className="absolute inset-0 animate-ping rounded-full bg-spectrum-yellow/30" />
            )}
            <VolumeX size={20} className="relative text-ink-300" />
          </>
        )}
      </motion.button>
    </>
  );
}
